import gsap from "@lib/gsap";
import { onPageIn, onDestroy } from "./_";
import { animSplitSlideUp } from "@/animations/split";

export default function (element: HTMLElement) {
  animSplitSlideUp(element);
  const imageWrap = element.querySelector(".mission_image_wrap") as HTMLElement;
  if (!imageWrap) return;

  imageWrap.style.overflow = "hidden";

  // 18 images arranged in an elliptical ring matching the design layout
  // Positions as % of container, sizes in px, r = rotation degrees
  const positions = [
    // Top row, left to right (5)
    { x: 4, y: 5, w: 150, h: 188, r: 2 },
    { x: 17, y: 2, w: 122, h: 153, r: -3 },
    { x: 35, y: 6, w: 135, h: 169, r: 5 },
    { x: 55, y: 3, w: 115, h: 144, r: -2 },
    { x: 74, y: 5, w: 130, h: 163, r: 3 },
    // Right side, top to bottom (3)
    { x: 86, y: 18, w: 125, h: 156, r: -5 },
    { x: 82, y: 42, w: 145, h: 181, r: 3 },
    { x: 85, y: 62, w: 120, h: 150, r: -3 },
    // Bottom row, right to left (5)
    { x: 75, y: 78, w: 130, h: 163, r: 4 },
    { x: 58, y: 82, w: 115, h: 144, r: -4 },
    { x: 40, y: 79, w: 140, h: 175, r: 2 },
    { x: 20, y: 82, w: 118, h: 148, r: -3 },
    { x: 4, y: 77, w: 128, h: 160, r: 4 },
    // Left side, bottom to top (3)
    { x: 1, y: 56, w: 135, h: 169, r: -4 },
    { x: 2, y: 34, w: 148, h: 185, r: 3 },
    { x: 3, y: 16, w: 125, h: 156, r: -2 },
    // Inner flanks (2)
    { x: 22, y: 20, w: 108, h: 135, r: 4 },
    { x: 70, y: 68, w: 112, h: 140, r: -4 },
  ];

  const images: HTMLImageElement[] = [];

  positions.forEach((p, i) => {
    const img = document.createElement("img");
    img.src = `https://picsum.photos/seed/mission${i}/300/375`;
    img.style.cssText = `
      position: absolute;
      width: ${p.w}px;
      height: ${p.h}px;
      object-fit: cover;
      left: ${p.x}%;
      top: ${p.y}%;
      transform: rotate(${p.r}deg);
      border-radius: 6px;
      pointer-events: none;
      will-change: transform, opacity;
    `;
    imageWrap.appendChild(img);
    images.push(img);
  });

  // Subtle nudge on individual image hover
  images.forEach((img, i) => {
    const p = positions[i];
    img.style.pointerEvents = "auto";

    img.addEventListener("mousemove", (e) => {
      const rect = img.getBoundingClientRect();
      const dx = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
      const dy = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
      gsap.to(img, {
        x: dx * 8,
        y: dy * 8,
        rotation: p.r + dx * 2,
        duration: 0.6,
        ease: "power2.out",
        overwrite: "auto",
      });
    });

    img.addEventListener("mouseleave", () => {
      gsap.to(img, {
        x: 0,
        y: 0,
        rotation: p.r,
        duration: 0.8,
        ease: "power2.out",
        overwrite: "auto",
      });
    });
  });

  // Random fade in/out: always keep at least 12 visible (max 6 faded out)
  const maxFaded = 6;
  const fadedOut = new Set<number>();
  let delayedCall: gsap.core.Tween | null = null;

  function cycleFade() {
    const targetCount = 4 + Math.floor(Math.random() * 3); // 4-6 faded out
    const currentlyFaded = [...fadedOut];
    const currentlyVisible = images
      .map((_, i) => i)
      .filter((i) => !fadedOut.has(i));

    // Fade in some currently faded
    const toFadeIn = currentlyFaded.filter(() => Math.random() < 0.4);

    // Calculate how many new to fade out, capped to keep ≥12 visible
    const stillFaded = currentlyFaded.filter((i) => !toFadeIn.includes(i));
    const needed = Math.min(
      Math.max(0, targetCount - stillFaded.length),
      maxFaded - stillFaded.length,
    );

    // Pick random visible ones to fade out
    const shuffled = [...currentlyVisible].sort(() => Math.random() - 0.5);
    const toFadeOut = shuffled.slice(0, Math.max(0, needed));

    toFadeIn.forEach((i) => {
      fadedOut.delete(i);
      gsap.to(images[i], { opacity: 1, duration: 1.5, ease: "power2.inOut" });
    });

    toFadeOut.forEach((i) => {
      fadedOut.add(i);
      gsap.to(images[i], { opacity: 0, duration: 1.5, ease: "power2.inOut" });
    });

    delayedCall = gsap.delayedCall(2.5 + Math.random() * 2, cycleFade);
  }

  onPageIn(async () => {
    // Start with 5 random images faded out (13 visible)
    const indices = [...Array(18).keys()].sort(() => Math.random() - 0.5);
    indices.slice(0, 8).forEach((i) => {
      fadedOut.add(i);
      gsap.set(images[i], { opacity: 0 });
    });

    delayedCall = gsap.delayedCall(2, cycleFade);
  });

  onDestroy(() => {
    delayedCall?.kill();
    images.forEach((img) => {
      gsap.killTweensOf(img);
      img.remove();
    });
  });
}
